import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import {
  getEventoVisibleByIdParaSocio,
  hidratarEventosPublicos,
} from "@/data/adminEventosStore";
import { joinFila } from "@/data/smartQueueStore";
import { obtenerCompraContexto } from "@/data/comprasStore";
import { toast } from "sonner";
import EventoDetalleView from "@/screens/app/EventoDetalleView";

/**
 * Detalle del Evento · portal socio real.
 *
 * Sprint 48 · al pulsar "Comprar", solicita ingreso a la fila real.
 * Si el backend responde `irASeleccionMesa=true`, navega directo a
 * selección de mesa sin mostrar la pantalla de fila. Si hay
 * compradores delante, dirige a la pantalla Smart Queue.
 */
export default function EventoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hidratado, setHidratado] = useState(false);
  const [uniendo, setUniendo] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    // Sprint 54 · Fase A · Fix bloqueo compra→mesa: reinicia el flag al
    // montar para que el doble-invoke de StrictMode (mount→cleanup→remount)
    // no deje `mountedRef` en false y bloquee la navegación tras joinFila.
    mountedRef.current = true;
    hidratarEventosPublicos().finally(() => setHidratado(true));
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const evento = getEventoVisibleByIdParaSocio(id);

  if (!evento) {
    if (!hidratado) return null;
    return <Navigate to="/app" replace />;
  }

  const onComprar = async () => {
    if (uniendo) return;
    setUniendo(true);

    // Sprint 55 · Límite acumulado ANTES de entrar a la fila / selección de
    // mesas. Si el socio ya alcanzó/superó el límite, no se permite avanzar.
    const ctx = await obtenerCompraContexto(evento.id);
    if (!mountedRef.current) return;
    if (
      ctx &&
      !ctx.error &&
      ctx.cupoRestante !== null &&
      ctx.cupoRestante !== undefined &&
      ctx.cupoRestante <= 0
    ) {
      setUniendo(false);
      toast.error(
        `Ya tienes ${ctx.confirmados} boleto(s) confirmado(s) y el límite ` +
          `de este evento es de ${ctx.limiteBoletosPorUsuario} por socio. ` +
          `No puedes adquirir más.`,
      );
      return;
    }

    const res = await joinFila(evento.id);
    if (!mountedRef.current) return;
    setUniendo(false);
    if (res && !res.error) {
      // El backend decide el destino sin dejar espacio para mocks.
      // Sprint 57 · F4B · el destino de selección depende de la modalidad real.
      const destinoSeleccion =
        evento.modalidad === "ACCESO_GENERAL" ? "acceso-general" : "seleccion-mesa";
      if (res.irASeleccionMesa) {
        navigate(`/app/eventos/${evento.id}/${destinoSeleccion}`, { replace: true });
      } else {
        navigate(`/app/eventos/${evento.id}/smart-queue`);
      }
      return;
    }
    if (res?.error === "MEMBRESIA_BLOQUEADA") {
      toast.error(res.mensaje);
      return;
    }
    if (res?.error === "LIMITE_ALCANZADO") {
      toast.error(res.mensaje || "Alcanzaste el límite de boletos de este evento.");
      return;
    }
    if (res?.error === "EVENTO_NO_ENCONTRADO") {
      toast.error("Este evento ya no está disponible.");
      navigate("/app", { replace: true });
      return;
    }
    toast.error(res?.mensaje || "No pudimos ingresarte a la fila. Reintenta.");
  };

  return (
    <EventoDetalleView
      evento={evento}
      mode="socio"
      onBack={() => navigate("/app")}
      onComprar={onComprar}
      comprando={uniendo}
    />
  );
}
